import React, { useCallback, useState } from 'react';
import {
  sendChatQuestion,
  type ChatQuestion,
  type ChatQuestionCreatedPayload,
} from '../../../../api/chatQuestions';
import ChatQuestionComposerModal, {
  type ChatQuestionComposerInput,
} from './ChatQuestionComposerModal';

type BeforeSendInput = {
  type: 'question';
  text: string;
  questionText: string;
  scope: ChatQuestion['scope'];
  eventId?: string | null;
  groupThreadId?: string | null;
  senderType: string;
};

type SentInput = {
  type: 'question';
  question: ChatQuestion;
  messageId: string;
};

type Params = {
  topicSendFields?: Record<string, unknown>;
  onTopicSent?: (result: ChatQuestionCreatedPayload) => unknown;
  enabled: boolean;
  readOnly: boolean;
  notifyBlocked: () => void;
  clearErrors: () => void;
  initialSupplementalMessage: string;
  scope: ChatQuestion['scope'];
  artistId?: string | null;
  eventId?: string | null;
  groupThreadId?: string | null;
  senderType: string;
  onBeforeSend?: (input: BeforeSendInput) => boolean | void | Promise<boolean | void>;
  onSent?: (input: SentInput) => void | Promise<void>;
  onQuestionSent: () => void;
};

type Result = {
  enabled: boolean;
  open: () => void;
  node: React.ReactNode;
};

export default function useChatQuestionComposer({
  topicSendFields,
  onTopicSent,
  enabled,
  readOnly,
  notifyBlocked,
  clearErrors,
  initialSupplementalMessage,
  scope,
  artistId,
  eventId,
  groupThreadId,
  senderType,
  onBeforeSend,
  onSent,
  onQuestionSent,
}: Params): Result {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const openComposer = useCallback(() => {
    clearErrors();
    if (readOnly) {
      notifyBlocked();
      return;
    }
    setOpen(true);
  }, [clearErrors, notifyBlocked, readOnly]);

  const submit = useCallback(async (input: ChatQuestionComposerInput) => {
    setSubmitting(true);
    try {
      if (onBeforeSend) {
        const shouldSend = await onBeforeSend({
          type: 'question',
          text: input.supplementalMessage,
          questionText: input.questionText,
          scope,
          eventId,
          groupThreadId,
          senderType,
        });
        if (shouldSend === false) return;
      }
      const result: ChatQuestionCreatedPayload = await sendChatQuestion({
        scope,
        artistId,
        eventId,
        groupThreadId,
        ...input,
        ...topicSendFields,
      });
      await onTopicSent?.(result);
      setOpen(false);
      onQuestionSent();
      await onSent?.({
        type: 'question',
        question: result.question,
        messageId: result.messageId,
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    topicSendFields,
    onTopicSent,
    artistId,
    eventId,
    groupThreadId,
    onBeforeSend,
    onQuestionSent,
    onSent,
    scope,
    senderType,
  ]);

  return {
    enabled,
    open: openComposer,
    node: (
      <ChatQuestionComposerModal
        open={open}
        initialSupplementalMessage={initialSupplementalMessage}
        submitting={submitting}
        onClose={() => setOpen(false)}
        onSubmit={submit}
      />
    ),
  };
}
